<template>
  <div v-if="hasPhotos" class="clinic-gallery" :class="{'clinic-gallery--mosaic': variant === 'mosaic'}">
    <!-- Основная галерея -->
    <div class="clinic-gallery__wrap" ref="galleryWrapper">
      <button
          type="button"
          v-for="(image, index) in visiblePhotos"
          :key="index"
          class="clinic-gallery__item"
          @click="openModal(index)"
      >
        <NuxtImg
            class="clinic-gallery__image"
            :alt="imageAlt(index)"
            :src="image"
            width="640"
            height="260"
            format="webp"
            sizes="sm:100vw md:640px lg:640px"
            loading="lazy"
        />
      </button>
    </div>

    <!-- Навигационные кнопки -->
    <button
        v-if="showSliderChrome"
        class="clinic-gallery__button clinic-gallery__prev"
        @click="handleNavClick(true)"
        :style="{ display: isPrevVisible ? 'block' : 'none' }"
        aria-label="Previous slide"
    />

    <button
        v-if="showSliderChrome"
        class="clinic-gallery__button clinic-gallery__next"
        @click="handleNavClick(false)"
        :style="{ display: isNextVisible ? 'block' : 'none' }"
        aria-label="Next slide"
    />

    <!-- Пагинация -->
    <div v-if="showSliderChrome && showPagination" class="clinic-gallery__pagination">
      <button
          v-for="(_, index) in visiblePhotos"
          :key="`pagin-${index}`"
          class="clinic-gallery__pagination-item"
          :class="{ 'active': activeIndex === index }"
          @click="handlePaginationClick(index)"
          :aria-label="`Go to slide ${index + 1}`"
      />
    </div>

    <!-- Модальное окно -->
    <div v-if="isModalOpen" class="clinic-gallery-modal" @click.self="closeModal">
      <div class="clinic-gallery-modal__content">
        <button
            class="clinic-gallery-modal__close"
            @click="closeModal"
            aria-label="Close modal"
        >
          &times;
        </button>

        <div class="clinic-gallery-modal__image-wrap">
          <NuxtImg
              class="clinic-gallery-modal__image"
              :alt="imageAlt(currentModalIndex)"
              :src="photos[currentModalIndex]"
              loading="eager"
          />
        </div>

        <div class="clinic-gallery-modal__controls">
          <button
              class="clinic-gallery-modal__nav clinic-gallery-modal__nav--prev"
              @click.stop="prevModalImage"
              :disabled="currentModalIndex === 0"
              aria-label="Previous image"
          >
            ←
          </button>

          <span class="clinic-gallery-modal__counter">
            {{ currentModalIndex + 1 }} / {{ photos.length }}
          </span>

          <button
              class="clinic-gallery-modal__nav clinic-gallery-modal__nav--next"
              @click.stop="nextModalImage"
              :disabled="currentModalIndex === photos.length - 1"
              aria-label="Next image"
          >
            →
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

interface Props {
  photos: string[];
  clinicName?: string;
  showNavigation?: boolean;
  showPagination?: boolean;
  slidesToScroll?: number;
  variant?: 'slider' | 'mosaic';
}

const props = withDefaults(defineProps<Props>(), {
  clinicName: '',
  showNavigation: true,
  showPagination: true,
  slidesToScroll: 1,
  variant: 'slider',
});

const MOSAIC_LIMIT = 5;
const visiblePhotos = computed(() =>
    props.variant === 'mosaic' ? props.photos.slice(0, MOSAIC_LIMIT) : props.photos,
);
const showSliderChrome = computed(() =>
    props.variant !== 'mosaic'
    && props.showNavigation
    && props.photos.length > 1,
);

const galleryWrapper = ref<HTMLDivElement | null>(null);
const activeIndex = ref(0);
const isPrevVisible = ref(false);
const isNextVisible = ref(false);
const slideWidth = ref(0);
const isModalOpen = ref(false);
const currentModalIndex = ref(0);

const hasPhotos = computed(() => props.photos?.length > 0);

const imageAlt = (index: number) =>
    props.clinicName
        ? `Фото клиники ${props.clinicName} ${index + 1}`
        : `Фото галереи ${index + 1}`;

// Основные функции галереи
const updateScrollBounds = () => {
  if (!galleryWrapper.value) return;

  const maxScrollLeft = galleryWrapper.value.scrollWidth - galleryWrapper.value.clientWidth;

  if (maxScrollLeft <= 0) {
    isPrevVisible.value = false;
    isNextVisible.value = false;
    return;
  }

  isPrevVisible.value = galleryWrapper.value.scrollLeft > 0;
  isNextVisible.value = galleryWrapper.value.scrollLeft < maxScrollLeft;
};

const updateActiveIndex = () => {
  if (!galleryWrapper.value || slideWidth.value === 0) return;
  activeIndex.value = Math.round(galleryWrapper.value.scrollLeft / slideWidth.value);
};

const scrollToSlide = (index: number) => {
  if (!galleryWrapper.value) return;

  const newIndex = Math.max(0, Math.min(index, visiblePhotos.value.length - 1));
  const targetPosition = newIndex * slideWidth.value;

  galleryWrapper.value.scrollTo({
    left: targetPosition,
    behavior: 'smooth',
  });
};

const handleNavClick = (isPrev: boolean) => {
  const direction = isPrev ? -1 : 1;
  const newIndex = activeIndex.value + (direction * props.slidesToScroll);
  scrollToSlide(newIndex);
};

const handlePaginationClick = (index: number) => {
  if (index !== activeIndex.value) {
    scrollToSlide(index);
  }
};

const handleScroll = () => {
  updateActiveIndex();
  updateScrollBounds();
};

const initGallery = () => {
  if (!galleryWrapper.value || !hasPhotos.value) return;

  const firstSlide = galleryWrapper.value.querySelector('.clinic-gallery__item');
  if (firstSlide) {
    slideWidth.value = firstSlide.getBoundingClientRect().width;
  }

  updateScrollBounds();
};

// Функции модального окна
const openModal = (index: number) => {
  currentModalIndex.value = index;
  isModalOpen.value = true;
  document.body.style.overflow = 'hidden';
};

const closeModal = () => {
  isModalOpen.value = false;
  document.body.style.overflow = '';
};

const prevModalImage = () => {
  if (currentModalIndex.value > 0) {
    currentModalIndex.value--;
  }
};

const nextModalImage = () => {
  if (currentModalIndex.value < props.photos.length - 1) {
    currentModalIndex.value++;
  }
};

const handleKeydown = (e: KeyboardEvent) => {
  if (!isModalOpen.value) return;

  switch (e.key) {
    case 'Escape':
      closeModal();
      break;
    case 'ArrowLeft':
      prevModalImage();
      break;
    case 'ArrowRight':
      nextModalImage();
      break;
  }
};

// Хуки жизненного цикла
onMounted(() => {
  initGallery();
  window.addEventListener('resize', initGallery);
  window.addEventListener('keydown', handleKeydown);

  if (galleryWrapper.value) {
    galleryWrapper.value.addEventListener('scroll', handleScroll);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', initGallery);
  window.removeEventListener('keydown', handleKeydown);

  if (galleryWrapper.value) {
    galleryWrapper.value.removeEventListener('scroll', handleScroll);
  }

  document.body.style.overflow = '';
});
</script>

<style lang="scss">
.clinic-gallery {
  position: relative;
  margin-bottom: 16px;
  border-radius: 16px;

  &__wrap {
    display: flex;
    overflow-x: auto;
    width: 100%;
    scrollbar-width: none;
    border-radius: 16px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }

    & > * {
      scroll-snap-align: start;
    }
  }

  &__item {
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    display: block;
    width: 100%;
    flex: 0 0 100%;
    flex-shrink: 0;
    cursor: pointer;
  }

  &__image {
    display: block;
    width: 100%;
    height: 280px;
    object-fit: cover;
    border-radius: 16px;
    background: $color-dark-white;
  }

  &__button {
    width: 40px;
    height: 40px;
    cursor: pointer;
    background-color: $color-white;
    border: 1.25px solid $color-pink;
    border-radius: 50%;
    background-repeat: no-repeat;
    background-position: center;
    position: absolute;
    top: 50%;
    z-index: 2;
    transform: translateY(-50%);
    box-shadow: 0 4px 10px 0 rgb(138 138 176 / 7%);
    transition: border-color 0.3s;

    &:hover {
      border-color: $color-purple;
    }
  }

  &__prev {
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTEiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxMSAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTkuMDIyNDYgMS41TDEuNTIyNDYgOUw5LjAyMjQ2IDE2LjQiIHN0cm9rZT0iI0IyMEZDMCIgc3Ryb2tlLXdpZHRoPSIyLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K);
    left: 12px;
  }

    &__next {
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTEiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxMSAxOCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIuMDIyNDYgMS41TDkuNTIyNDYgOUwyLjAyMjQ2IDE2LjUiIHN0cm9rZT0iI0IyMEZDMCIgc3Ryb2tlLXdpZHRoPSIyLjUiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K);
    right: 12px;
  }

  &--mosaic {
    @include media(md, min-width) {
      .clinic-gallery__wrap {
        display: grid;
        grid-template-columns: 2fr 1fr 1fr;
        grid-template-rows: 140px 140px;
        gap: 8px;
        overflow: hidden;
        border-radius: 16px;
        scroll-snap-type: none;
      }

      .clinic-gallery__item {
        width: auto;
        height: 100%;
        min-height: 0;

        &:first-child {
          grid-row: 1 / 3;
        }
      }

      .clinic-gallery__image {
        height: 100%;
        border-radius: 0;
        aspect-ratio: auto;
      }

      .clinic-gallery__button,
      .clinic-gallery__pagination {
        display: none;
      }
    }

    @include media(md) {
      .clinic-gallery__wrap {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 90px 90px;
        gap: 8px;
        overflow: hidden;
        border-radius: 16px;
        scroll-snap-type: none;
      }

      .clinic-gallery__item {
        width: auto;
        height: 100%;

        &:first-child {
          grid-row: auto;
        }
      }

      .clinic-gallery__image {
        height: 100%;
        border-radius: 0;
        aspect-ratio: auto;
      }

      .clinic-gallery__button,
      .clinic-gallery__pagination {
        display: none;
      }
    }
  }

  &__pagination {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 10px;
    display: flex;
    justify-content: center;
    gap: 8px;
    width: 100%;
  }

  &__pagination-item {
    height: 3px;
    width: 40px;
    background-color: rgba(255, 255, 255, 0.75);
    cursor: pointer;
    border-radius: 3px;
    transition: all 0.3s;
    border: none;
    padding: 0;

    &.active,
    &:hover {
      background-color: $color-purple;
    }
  }

  @include media(sm) {
    border-radius: 14px;

    &__wrap,
    &__image {
      border-radius: 14px;
    }

    &__image {
      height: 180px;
    }

    &__pagination-item {
      width: 28px;
    }

    &__prev {
      left: 8px;
    }

    &__next {
      right: 8px;
    }

    &__button {
      width: 32px;
      height: 32px;
      background-size: 8px 12px;
    }
  }
}

/* Стили модального окна */
.clinic-gallery-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;

  &__content {
    position: relative;
    max-width: 90%;
    max-height: 90%;
    width: 100%;
  }

  &__close {
    position: absolute;
    top: -40px;
    right: 0;
    background: none;
    border: none;
    color: white;
    font-size: 30px;
    cursor: pointer;
    padding: 5px;
    z-index: 2;

    &:hover {
      color: $color-accent-text;
    }
  }

  &__image-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  &__image {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
    border-radius: 8px;
  }

  &__controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin-top: 20px;
    color: white;
  }

  &__nav {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 20px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover:not(:disabled) {
      background: rgba(178, 15, 192, 0.7);
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  &__counter {
    font-size: 16px;
    min-width: 60px;
    text-align: center;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@media screen and (max-width: 767px) {
  .clinic-gallery-modal {
    padding: 10px;

    &__close {
      top: -30px;
      font-size: 24px;
    }

    &__nav {
      width: 32px;
      height: 32px;
      font-size: 16px;
    }

    &__counter {
      font-size: 14px;
    }
  }
}
</style>